import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { RulePreviewComponent } from '../../common/rule-preview/rule-preview.component';
import { Filter, PreviewCustomRule } from '../../models/filter-models';
import { CosmeticPresetsService } from '../../services/cosmetic-presets.service';
import { poe2 } from '../../../game';
import { DefaultLabelSize, DEFAULT_LABEL_SIZE_OPTIONS, DEFAULT_LABEL_SIZE_LABELS } from '../../models/enums';

type StartingTier = 'sTier' | 'aTier' | 'bTier' | 'cTier';

const STARTING_TIER_OPTIONS: StartingTier[] = ['sTier', 'aTier', 'bTier', 'cTier'];
const STARTING_TIER_LABELS: Record<StartingTier, string> = {
  sTier: 'S-Tier',
  aTier: 'A-Tier',
  bTier: 'B-Tier',
  cTier: 'C-Tier',
};

@Component({
  selector: 'app-cosmetic-presets',
  imports: [RulePreviewComponent],
  templateUrl: './cosmetic-presets.component.html',
  styleUrl: './cosmetic-presets.component.scss',
  host: {
    '[class.tutorial]': 'true',
    '[class.center]': 'true',
    'style': 'max-width: max-content',
  }
})
export class CosmeticPresetsComponent implements OnChanges {
  @Output() close = new EventEmitter<void>();
  @Output() updateFilter = new EventEmitter<void>();
  @Input({ required: true }) filter!: Filter;

  currencyRules: PreviewCustomRule[] = [];
  uniqueRules: PreviewCustomRule[] = [];

  defaultLabelSizeIndex = 3;
  defaultLabelSizeLabel = DEFAULT_LABEL_SIZE_LABELS.default;
  startingTierIndex = 2;
  startingTierLabel = STARTING_TIER_LABELS.bTier;
  showStartingTierSlider = false;

  constructor(private elementRef: ElementRef, public cosmeticPresetsService: CosmeticPresetsService) {
    this.currencyRules = this.cosmeticPresetsService.currencyRules;
    this.uniqueRules = this.cosmeticPresetsService.uniqueRules;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.updateDefaultLabelSizeDisplay();
      this.updateStartingTierDisplay();
      this.updateStartingTierSliderVisibility();
      this.cosmeticPresetsService.regenerateRules(this.filter);
    }
  }

  updateStartingTierDisplay() {
    const tier = this.filter.cosmeticPresets.startingTier;
    this.startingTierIndex = STARTING_TIER_OPTIONS.indexOf(tier);
    if (this.startingTierIndex < 0) {
      this.startingTierIndex = 2;
    }
    this.startingTierLabel = STARTING_TIER_LABELS[STARTING_TIER_OPTIONS[this.startingTierIndex]];
  }

  updateStartingTierSliderVisibility() {
    this.showStartingTierSlider = this.filter.cosmeticPresets.labelColor !== 'default' || this.filter.cosmeticPresets.labelSize !== 'default';
  }

  onStartingTierChange(event: Event) {
    const index = Number((event.target as HTMLInputElement).value);
    this.startingTierIndex = index;
    this.startingTierLabel = STARTING_TIER_LABELS[STARTING_TIER_OPTIONS[index]];
    this.cosmeticPresetsService.setStartingTier(STARTING_TIER_OPTIONS[index], this.filter);
  }

  updateDefaultLabelSizeDisplay() {
    const size = this.filter.cosmeticPresets.defaultLabelSize;
    this.defaultLabelSizeIndex = DEFAULT_LABEL_SIZE_OPTIONS.indexOf(size);
    if (this.defaultLabelSizeIndex < 0) {
      this.defaultLabelSizeIndex = 3;
    }
    this.defaultLabelSizeLabel = DEFAULT_LABEL_SIZE_LABELS[DEFAULT_LABEL_SIZE_OPTIONS[this.defaultLabelSizeIndex]];
  }

  onDefaultLabelSizeChange(event: Event) {
    const index = Number((event.target as HTMLInputElement).value);
    this.setDefaultLabelSize(DEFAULT_LABEL_SIZE_OPTIONS[index]);
  }

  setLabelColor(color: 'default' | 'accentNeutral' | 'accentTiered') {
    this.cosmeticPresetsService.setLabelColor(color, this.filter);
    this.updateStartingTierSliderVisibility();
  }

  setLabelSize(size: 'default' | 'larger' | 'veryLarge') {
    this.cosmeticPresetsService.setLabelSize(size, this.filter);
    this.updateStartingTierSliderVisibility();
  }

  setDefaultLabelSize(size: DefaultLabelSize) {
    this.cosmeticPresetsService.setDefaultLabelSize(size, this.filter);
    this.updateDefaultLabelSizeDisplay();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDialog();
    }
  }

  closeDialog() {
    this.close.emit();
  }
}
